import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../global/Modal';
import { outfitAdminApi } from '../../../services/outfitAdminApi';

function TrendLookFeedModal({ isOpen, onClose, trend }) {
  const [loading, setLoading] = useState(false);
  const [feedData, setFeedData] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const limit = 8;

  const fetchLooks = useCallback(async () => {
    if (!trend?.outfit_id) return;
    try {
      setLoading(true);
      setError('');
      const response = await outfitAdminApi.fetchTrendLooks(trend.outfit_id, page, limit);
      if (response && response.data) {
        setFeedData(response.data);
      }
    } catch (err) {
      console.error('Failed to load trend looks feed:', err);
      setError(err.message || 'Failed to load trend looks feed');
    } finally {
      setLoading(false);
    }
  }, [trend?.outfit_id, page, limit]);

  useEffect(() => {
    if (isOpen && trend?.outfit_id) {
      setPage(1);
      fetchLooks();
    } else {
      setFeedData(null);
      setError('');
    }
  }, [isOpen, trend?.outfit_id, fetchLooks]);

  useEffect(() => {
    if (isOpen && trend?.outfit_id) {
      fetchLooks();
    }
  }, [page, fetchLooks, isOpen, trend?.outfit_id]);

  const looks = feedData?.looks || [];
  const pagination = feedData?.pagination || { page: 1, limit: 8, total_looks: 0, total_pages: 1 };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Trend Feed: ${trend?.name || 'Trend Looks'}`}
    >
      <div className="look-feed-modal-content">
        {error && (
          <div className="outfit-alert error">
            <span>{error}</span>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span className={`cci-gender-badge ${trend?.gender || 'men'}`}>
              {trend?.gender}
            </span>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              Handle: <code>{trend?.handle}</code>
            </span>
          </div>
          <span className="wizard-looks-badge">
            {pagination.total_looks} Curated Look{pagination.total_looks === 1 ? '' : 's'}
          </span>
        </div>

        {loading ? (
          <div className="outfit-loading-state" style={{ minHeight: '300px' }}>
            <div className="outfit-spinner" />
            <p>Hydrating product assets for curated looks...</p>
          </div>
        ) : looks.length === 0 ? (
          <div className="outfit-empty-state" style={{ padding: '50px 20px' }}>
            <p>No hydrated looks available for this trend.</p>
          </div>
        ) : (
          <div className="look-feed-grid">
            {looks.map((look, lIdx) => {
              const lookNumber = (pagination.page - 1) * pagination.limit + lIdx + 1;
              const occasionStr = Array.isArray(look.occasion) ? look.occasion.join(', ') : look.occasion;

              return (
                <div key={look.look_unique_id || lIdx} className="look-feed-card">
                  <div className="look-card-header">
                    <span className="look-number">Look #{lookNumber}</span>
                    {occasionStr && (
                      <span className="cci-occasion-tag" style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {occasionStr}
                      </span>
                    )}
                  </div>

                  <div className="look-components-list">
                    {look.outfit_components &&
                      look.outfit_components.map((comp, cIdx) => {
                        const product = comp.products?.[0];
                        const imgUrl = product?.images?.[0] || '';

                        return (
                          <div key={cIdx} className="look-component-item">
                            {imgUrl ? (
                              <img
                                src={imgUrl}
                                alt={product?.title || comp.component_name}
                                className="look-product-img"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="look-product-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: '#94a3b8' }}>
                                No Img
                              </div>
                            )}

                            <div className="look-product-details">
                              <span className="look-component-slot">
                                {comp.expected_category || comp.component_name}
                              </span>
                              <span className="look-product-title">
                                {product?.title || comp.component_name}
                              </span>
                              {product?.brand && (
                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                  {product.brand}
                                </span>
                              )}
                              {product?.price !== undefined && product?.price !== null && (
                                <span className="look-product-price">
                                  ₹{Number(product.price).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal Pagination */}
        {pagination.total_pages > 1 && (
          <div className="look-pagination">
            <button
              className="outfit-btn outfit-btn-secondary"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
            >
              &lt; Previous
            </button>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <button
              className="outfit-btn outfit-btn-secondary"
              disabled={page >= pagination.total_pages || loading}
              onClick={() => setPage((p) => Math.min(p + 1, pagination.total_pages))}
            >
              Next &gt;
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default TrendLookFeedModal;
